/**
 * SelectionAsk: a small "Ask the consultant" button that floats by the words a person selects on a page (next phase,
 * step 4c, 2026-10-06; Arjun, 2026-10-05: "if they drag and select say a defect explanation or part of the brief, if we
 * could show as the consultant as a small hover").
 *
 * It appears when the selection is words in the page's main content (not the panel, a field or an editable region:
 * lib/copilot/selection selectionTargetOf), on every page: a page that says what it is about (usePageCopilotContext's
 * `about`) gives the question its context, and one that does not is named by its own heading (selectionAboutOf;
 * Arjun, 2026-10-23: the chip was missing on the newer pages, which published no context). It shows just
 * above the selection, or below it where there is no room or the person touches the screen (chipPlacement). Clicking
 * it opens the panel (store.ask) with a question drafted and the page's context, the passage, and the keys of the card
 * the passage sits in (selectionRequest), and clears the selection.
 *
 * It goes away on Escape, on scroll or resize, when the selection collapses, and on a route change. It waits for the
 * pointer to come up before it shows (a chip under the pointer mid-drag is in the way), and for a touch selection, for
 * the handles to stop moving. CopilotPanel renders it, so it lives with the panel closed, where the consultant is open.
 *
 * From the keyboard (#1186 review LOW 3): when the chip shows, a polite announcement names its shortcut, Alt+Shift+A
 * (isAskShortcut), which asks as a click does. Only a primary press starts a drag (isPrimaryPress): a right-click on a
 * Mac sends no pointer-up, which would keep the chip away until the next click.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { useCopilot } from "@/lib/copilot/store";
import { selectionAboutOf } from "@/lib/copilot/ask";
import { SELECTION_CHIP_LAYER } from "@/lib/copilot/panel-layout";
import {
  ASK_SHORTCUT,
  PANEL_ATTR,
  chipPlacement,
  isAskShortcut,
  isPrimaryPress,
  keyUpShowsNoChip,
  selectionRequest,
  selectionTargetOf,
  type Area,
  type ElementLike,
  type Placement,
  type SelectionTarget,
} from "@/lib/copilot/selection";

/** How long a keyboard or touch selection is quiet before the chip shows, in milliseconds. */
const QUIET_MS = 200;
/** How long after a pointer comes up the selection has settled. */
const SETTLE_MS = 20;

interface Shown {
  target: SelectionTarget;
  placement: Placement;
  /** The page it was selected on: a route change hides it. */
  path: string;
}

/** The part of the screen the chip may sit in: the main content the selection is in, less the panel, within the window. */
function areaOf(selection: Selection): Area {
  const start = selection.anchorNode;
  const main = (start instanceof Element ? start : start?.parentElement)?.closest("main") ?? null;
  const box = main?.getBoundingClientRect();
  const panel = document.querySelector(`[${PANEL_ATTR}]`)?.getBoundingClientRect();
  const right = Math.min(box?.right ?? window.innerWidth, window.innerWidth);
  return {
    top: Math.max(box?.top ?? 0, 0),
    left: Math.max(box?.left ?? 0, 0),
    right: panel && panel.width > 0 ? Math.min(right, panel.left) : right,
    bottom: Math.min(box?.bottom ?? window.innerHeight, window.innerHeight),
  };
}

/** The page's own heading (PageHeader's h1 in the main content), as the chip names a page that says nothing of itself. */
function mainHeading(): string {
  return document.querySelector("main h1")?.textContent?.trim() ?? "";
}

export function SelectionAsk() {
  const copilot = useCopilot();
  const { pathname } = useLocation();
  const [shown, setShown] = useState<Shown | null>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  // What the shortcut asks, as of this render: the listeners below are set up once per page.
  const askRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    let pointerDown = false;
    let touch = false;

    const hide = () => setShown(null);
    const settle = () => {
      const selection = window.getSelection();
      const target = selectionTargetOf(selection, document.activeElement as ElementLike | null);
      if (!selection || !target) return hide();
      const placement = chipPlacement(target.rect, areaOf(selection), { prefer: touch ? "below" : "above" });
      setShown({ target, placement, path: pathname });
    };
    const later = (ms: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(settle, ms);
    };
    const onChipEvent = (e: Event) => e.target instanceof Node && chipRef.current?.contains(e.target) === true;

    const onSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        window.clearTimeout(timer);
        return hide();
      }
      // A mouse drag shows the chip when the pointer comes up; a keyboard or touch selection, once it is quiet.
      if (pointerDown) return hide();
      later(QUIET_MS);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (onChipEvent(e)) return;
      if (!isPrimaryPress(e)) return hide();
      pointerDown = true;
      touch = e.pointerType === "touch";
      window.clearTimeout(timer);
      hide();
    };
    const onPointerUp = (e: PointerEvent) => {
      if (onChipEvent(e)) return;
      // A right-click's pointer-up (Windows sends one) is no end of a drag: its menu is open (#1195 review LOW 1).
      if (!isPrimaryPress(e)) return;
      pointerDown = false;
      touch = e.pointerType === "touch";
      later(SETTLE_MS);
    };
    const onPointerCancel = () => {
      pointerDown = false;
    };
    // A context menu (a right-click, a Mac's Ctrl+click) ends any drag and keeps the chip away while it is open.
    const onContextMenu = () => {
      pointerDown = false;
      window.clearTimeout(timer);
      hide();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide();
      else if (isAskShortcut(e) && askRef.current) {
        e.preventDefault();
        askRef.current();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      // A menu key, a modifier released alone and Escape end nothing: a menu they opened stays open, with no chip
      // beside it (the contextmenu handler hid it).
      if (!keyUpShowsNoChip(e)) later(SETTLE_MS);
    };
    // Any scrolling region scrolls without bubbling: capture. The panel's own thread scrolls as messages come.
    const onScroll = (e: Event) => {
      if (e.target instanceof Element && e.target.closest(`[${PANEL_ATTR}]`)) return;
      hide();
    };

    document.addEventListener("selectionchange", onSelectionChange);
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", hide);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("selectionchange", onSelectionChange);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", hide);
    };
  }, [pathname]);

  // A selection made on another page shows nothing.
  const visible = shown && shown.path === pathname ? shown : null;
  const ask = visible
    ? () => {
        // The page's own context, else the page by its heading as it reads when the person asks.
        const about = selectionAboutOf(copilot.pageContext, pathname, mainHeading());
        copilot.ask(selectionRequest(about, visible.target));
        window.getSelection()?.removeAllRanges();
        setShown(null);
      }
    : null;
  useEffect(() => {
    askRef.current = ask;
  });
  // Said once as the chip shows, for a person who selected from the keyboard or reads with a screen reader.
  const announcement = (
    <span role="status" aria-live="polite" className="sr-only">
      {ask ? `Press ${ASK_SHORTCUT} to ask the consultant about the selected text.` : ""}
    </span>
  );
  if (!visible || !ask) return announcement;

  return (
    <>
      {announcement}
      <button
        ref={chipRef}
        type="button"
        data-selection-ask=""
        data-no-print=""
        data-html2canvas-ignore="true"
        data-side={visible.placement.side}
        aria-label="Ask the consultant about the selected text"
        aria-keyshortcuts={ASK_SHORTCUT}
        // A press on the chip must not move the focus or collapse the selection it was made for.
        onMouseDown={(e) => e.preventDefault()}
        onClick={ask}
        style={{ position: "fixed", top: visible.placement.top, left: visible.placement.left }}
        className={cn(
          SELECTION_CHIP_LAYER,
          "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-surface-elevated px-3",
          "text-xs font-medium text-on-surface shadow-lg transition-colors hover:bg-surface-muted",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        <Sparkles aria-hidden className="size-3.5 text-brand" />
        Ask the consultant
      </button>
    </>
  );
}
